import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import axios from "axios";
import DatePicker from "react-multi-date-picker";
import Avatar from "@material-ui/core/Avatar";
import excelIcon from "images/Microsoft_Office_Excel_Logo_24px.png";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { MdCake } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { theme } from "styles/theme";
import birthday from "../team/birthday.png";

/** Order: real team categories, then Unassigned / No Team Assigned, then Absent. */
const reorderHistoricalTeamCategories = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return rows;
  }
  const isAbsentRow = (c) =>
    Number(c.category_id) === -2 ||
    String(c.category_id) === "-2" ||
    c.category_label === "Absent";
  const isUnassignedRow = (c) =>
    Number(c.category_id) === -1 ||
    String(c.category_id) === "-1" ||
    c.category_label === "Unassigned" ||
    c.category_label === "No Team Assigned";
  const teams = rows.filter((c) => !isAbsentRow(c) && !isUnassignedRow(c));
  const unassigned = rows.filter(isUnassignedRow);
  const absent = rows.filter(isAbsentRow);
  const idx = (x) =>
    typeof x.category_index === "number" ? x.category_index : Number(x.category_index ?? 0);
  teams.sort((a, b) => idx(a) - idx(b));
  unassigned.sort((a, b) => idx(a) - idx(b));
  absent.sort((a, b) => idx(a) - idx(b));
  return [...teams, ...unassigned, ...absent];
};

const MainCon = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  background-image: linear-gradient(to bottom right, #014156, #370037);
  background-attachment: fixed;
  background-size: cover;
  position: relative;
  width: 100%;
  min-height: calc(100vh - ${theme.height.navigation});
  flex: 1;
  > div {
    display: flex;
    flex-direction: row;
    width: 100%;
  }
`;

const Con = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  flex: 1;
  overflow: auto;
  &::-webkit-scrollbar {
    background: black;
    border-radius: 10px;
    height: 5px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: #2c2c2c;
  }
  &::-webkit-scrollbar-track {
    border-radius: 25px;
    background: #cacaca;
  }
  > div {
    margin: 0.5rem;
    border-radius: 5px;
    padding: 0.5rem;
    width: 5;
  }
`;

const Category = styled.div`
  display: flex;
  flex-direction: column;
  background: #36495b;
  color: white;
  justify-content: flex-start;
  align-items: flex-start;
  flex: 0 0 220px;
  > div:last-child {
    min-height: 100px;
    min-width: 100%;
  }
`;

const ToolbarRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  box-sizing: border-box;
  ${(props) =>
    props.$plain
      ? `
    background: #fff;
    border-bottom: 1px solid #e5e7eb;
  `
      : `
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    color: #fff;
  `}
`;

const DescriptionBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  min-width: 0;
  max-width: min(100%, 520px);
`;

const ToolbarTitle = styled.span`
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.3;
  ${(props) => (props.$plain ? "color: #111827;" : "color: #fff;")}
`;

const ToolbarSubtitle = styled.span`
  font-size: 0.8125rem;
  line-height: 1.35;
  margin-top: 0.15rem;
  ${(props) =>
    props.$plain ? "color: #4b5563;" : "color: rgba(255, 255, 255, 0.88);"}
`;

const DatePickerSlot = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-left: 10px;
`;

const ToolbarActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-left: auto;
  flex-shrink: 0;
`;

const ActionButtonPair = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  margin: 0.15rem 0 0.15rem 0.25rem;
  & > button:first-of-type {
    border-radius: 5px 0 0 5px;
    border-right: 1px solid rgba(128, 128, 128, 0.55);
  }
  & > button:last-of-type {
    border-radius: 0 5px 5px 0;
  }
`;

const ActionBtn = styled.button`
  font-size: 1rem;
  font-weight: bold;
  border: 0;
  cursor: pointer;
  padding: 0.5rem;
  outline: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  ${(props) =>
    props.$plain
      ? `
    background: #e5e7eb;
    color: #1f2937;
    &:hover {
      background: #d1d5db;
    }
  `
      : `
    background: rgba(0, 0, 0, 0.5);
    color: #fff;
    &:hover {
      background: rgba(0, 0, 0, 0.65);
    }
  `}
`;

const EmployeeCard = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #002c37;
  margin: 0.2rem 0 0.2rem 0;
  border-radius: 3px;
  padding: 0 0.1rem 0 1rem;
  justify-content: center;
`;

const UserCon = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: row;
  padding: 0.3rem 0 0.3rem 0;
  border-radius: 5px;
  color: white;
  position: relative;
  overflow: hidden;
  width: 100%;
  ${(props) =>
    props.$birthday &&
    `
    background-image: url(${birthday});
    background-size: cover;
    `}
`;

const Birthday = styled(MdCake)`
  position: absolute;
  display: ${(props) => props.$show};
  bottom: 0.1rem;
  left: 1.5rem;
  color: #ff5f5f;
`;

const UserDisc = styled.div`
  font-size: 0.8rem;
  margin-left: 0.5rem;
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  > div:first-child {
    font-size: 1rem;
  }
  > div:last-child {
    color: #dfdfdf;
  }
`;

const Child = styled.div`
  display: flex;
  padding: 0;
  margin: 0;
  margin-left: 0.1rem;
  flex-direction: column;
  margin-top: 1px;
`;

const ChildCon = styled.div`
  position: relative;
  padding: 0;
  margin: 0;
  max-width: 100%;
`;

const HistoricalTeam = () => {
  const { t } = useTranslation();
  const tBase = "views.historicalTeam.index";
  const tr = (key) => t(`${tBase}.${key}`);

  const [data, populateData] = useState([]);
  const [images, setImages] = useState({});
  const [date, setDate] = useState(new Date());
  const [plainTextView, setPlainTextView] = useState(false);

  const formatDate = (dateValue) => {
    const dateObj = dateValue instanceof Date ? dateValue : new Date(dateValue);
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    const year = dateObj.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const fetchData = useCallback(async () => {
    try {
      const formattedDate = formatDate(date);
      const yyyyMmDd = format(date instanceof Date ? date : new Date(date), "yyyy-MM-dd");
      const base = process.env.REACT_APP_SERVER_URL;
      const [histRes, teamRes] = await Promise.allSettled([
        axios.get(`${base}/api/v1/eserv-business/historicalteam?date=${formattedDate}`),
        axios.get(`${base}/api/v1/eserv-business/team?date=${yyyyMmDd}`),
      ]);
      if (histRes.status !== "fulfilled") {
        throw histRes.reason;
      }
      const raw = Array.isArray(histRes.value.data) ? histRes.value.data : [];
      populateData(reorderHistoricalTeamCategories(raw));
      if (teamRes.status === "fulfilled" && teamRes.value.data?.[1] && typeof teamRes.value.data[1] === "object") {
        setImages(teamRes.value.data[1]);
      } else {
        setImages({});
      }
    } catch (e) {
      alert(e);
      console.error(e);
    }
  }, [date]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onDateChange = (newDate) => {
    setDate(newDate);
  };

  const handleDownload = async () => {
    try {
      const formattedDate = formatDate(date);
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/historicalteam/download?date=${formattedDate}`,
        {
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `historical-team-${formattedDate.replace(/\//g, "-")}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading Excel file:", error);
      alert("Failed to download Excel file. Please try again.");
    }
  };

  const renderTopToolbar = (isPlain) => (
    <ToolbarRow $plain={isPlain}>
      <DescriptionBlock>
        <ToolbarTitle $plain={isPlain}>{tr("Historical Team")}</ToolbarTitle>
        <ToolbarSubtitle $plain={isPlain}>{tr("Select a date to view the team as it was on that day.")}</ToolbarSubtitle>
      </DescriptionBlock>
      <DatePickerSlot
        className={
          isPlain
            ? "[&_input]:text-black [&_input]:bg-white"
            : "!text-black [&_input]:!text-black [&_input]:!bg-white"
        }
      >
        <DatePicker
          value={date}
          onChange={onDateChange}
          format="MM/DD/YYYY"
          style={{ height: "30px" }}
          zIndex={1000}
          portal
          calendarPosition="bottom-start"
        />
      </DatePickerSlot>
      <ToolbarActions>
        <ActionButtonPair>
          {isPlain ? (
            <ActionBtn type="button" $plain={isPlain} onClick={() => setPlainTextView(false)}>
              {tr("View Styled")}
            </ActionBtn>
          ) : (
            <ActionBtn type="button" $plain={isPlain} onClick={() => setPlainTextView(true)}>
              {tr("View Plain Text")}
            </ActionBtn>
          )}
          <ActionBtn type="button" $plain={isPlain} onClick={() => handleDownload()} title={tr("Download")}>
            <img src={excelIcon} alt="" className="w-[17.5px] h-[17.5px]" />
            {tr("Download")}
          </ActionBtn>
        </ActionButtonPair>
      </ToolbarActions>
    </ToolbarRow>
  );

  const renderUserTree = (employee) => {
    const name = (employee["employee_name"] || "").toString();
    const jobTitle = (employee["employee_job_title"] || "").toString();
    const birthDate = employee["employee_birth_date"];
    const isToday =
      birthDate &&
      format(new Date(`${birthDate}T00:00`), "MM-dd") === format(new Date(), "MM-dd");
    const initials = name.match(/\b(\w)/g);
    const imageSrc = images[employee["employee_id"]];
    const subChildren = Array.isArray(employee["children"]) ? employee["children"] : [];

    return (
      <EmployeeCard>
        <Link
          to={`/admin/employees/${employee["employee_id"]}`}
          target="_blank"
          rel="noopener noreferrer"
          className="no-underline"
          style={{ color: "inherit" }}
        >
          <UserCon $birthday={isToday}>
            <div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <Avatar src={imageSrc} style={{ color: "white", background: "grey" }}>
                  {initials ? initials.join("") : ""}
                </Avatar>
                <Birthday $show={isToday ? "block" : "none"} />
                <UserDisc>
                  <div>{name}</div>
                  <div>{jobTitle}</div>
                </UserDisc>
              </div>
            </div>
          </UserCon>
        </Link>
        {subChildren.length > 0 ? (
          <Child>
            {subChildren.map((child) => (
              <ChildCon key={child["employee_id"]}>{renderUserTree(child)}</ChildCon>
            ))}
          </Child>
        ) : null}
      </EmployeeCard>
    );
  };

  const renderCategoryBody = (category) => {
    const children = Array.isArray(category["child"]) ? category["child"] : [];
    return children.map((parent) => (
      <React.Fragment key={parent["employee_id"]}>{renderUserTree(parent)}</React.Fragment>
    ));
  };

  if (plainTextView) {
    return (
      <div className="flex flex-col w-full min-h-[calc(100vh-50px)] flex-1 bg-gray-50">
        {renderTopToolbar(true)}
        <div className="flex flex-row flex-wrap gap-4 w-full p-4">
          {data.map((category) => {
            const children = Array.isArray(category["child"]) ? category["child"] : [];
            return (
              <div
                key={category["category_id"] ?? "no-team"}
                className="min-w-[160px] flex flex-col border border-gray-300 rounded-md bg-white p-3"
              >
                <div className="text-lg font-bold text-gray-900">{category["category_label"]}</div>
                {children.map((parent) => {
                  const subChildren = Array.isArray(parent["children"]) ? parent["children"] : [];
                  return (
                    <div key={parent["employee_id"]} className="text-blue-600 mt-1">
                      {parent["employee_name"]}
                      {subChildren.map((child) => (
                        <ul key={child["employee_id"]} className="text-green-700 ml-4">
                          <li className="list-disc list-inside">{child["employee_name"]}</li>
                        </ul>
                      ))}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <MainCon>
      {renderTopToolbar(false)}
      <Con>
        {data.map((category) => (
          <Category key={category["category_id"] ?? "no-team"}>
            <div style={{ width: "100%" }}>
              <p style={{ display: "inline", margin: 0 }}>{category["category_label"]}</p>
            </div>
            <div>{renderCategoryBody(category)}</div>
          </Category>
        ))}
      </Con>
    </MainCon>
  );
};

export default HistoricalTeam;
